import { PushNotifications } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { notifications } from '~api';
import router from '~router';
import { isNative, nativePlatform } from './Platform';

/**
 * Native push notification service. Talks to the existing backend
 * `POST /profile/devices` + `DELETE /profile/devices` which forwards the token
 * to AWS SNS (APNS platform endpoint on iOS, GCM/FCM on Android).
 *
 * Separate from the OneSignal web-push wiring in public/index.html +
 * src/main.js — the two pipelines never collide because this one bails out on
 * the web build via `isNative()`.
 *
 * Lifecycle:
 *   1. App.vue calls `initPushnotifications()` once, after the store is ready.
 *      It requests OS permission, registers with APNs/FCM, and listens for
 *      token + incoming push events.
 *   2. When the user logs in, Dashboard/index.vue calls `registerDevice()`
 *      which POSTs the stored token to the backend with an auth bearer.
 *   3. On logout the auth store calls `removePushnotifications()` which
 *      DELETEs the device server-side and clears the cached token.
 */

// Preferences keys
const KEY_TOKEN = 'push.token';
const KEY_PLATFORM = 'push.platform';
const KEY_PENDING_DEEP_LINK = 'push.pendingDeepLink';
const KEY_PERMISSION = 'push.permission';
// Remember which user the token was last registered for so we can detect
// account switches (same physical device, different login) and force a
// re-register on the server. The backend reassigns device ownership when
// this POST arrives, so the previous user stops receiving our pushes.
const KEY_REGISTERED_USER = 'push.registeredUser';

let listenersAttached = false;
let resumeHandlerAttached = false;
// In-flight guard so a burst of `registration` events and explicit
// registerDevice calls never hit the backend concurrently with conflicting
// user IDs mid-login.
let registerInFlight = null;

/**
 * Route to the appropriate session view when a push payload carries one.
 * Mirrors the semantics of the old Cordova PushNotifications service so
 * existing backend payload shapes keep working.
 *
 * The backend sets `action` based on `session.mode` (see
 * user.reminder.service.ts → sendPushToDevice):
 *   • mode === 'minute' → action: 'minutes' → /mindful-moments/view/:id
 *   • everything else   → action: 'sessions' → /sessions/view/:id
 *
 * This matches the in-app FeedItem / Banner navigation. Older payloads
 * may omit `action`, so we fall back to the meditation route.
 */
function routeForAction(action, id) {
    const name =
        action === 'minutes'
            ? 'dashboard.mindfulMinutesItem'
            : 'dashboard.sessions.view';
    return { name, params: { id } };
}

function routeFromPayload(data = {}) {
    // Direct sessionId key (new backend shape)
    if (data.sessionId) {
        return routeForAction(data.action, data.sessionId);
    }
    // New backend shape: top-level `url` (GCM data + APNS root keys).
    if (data.url) {
        return routeForAction(data.action, data.url);
    }
    // Backward compat with in-flight pushes from the old APNs payload
    // shape that nested `url` inside `aps`. Capacitor's iOS bridge
    // exposes the full userInfo dict (aps included) on `notification.data`.
    if (data.aps && typeof data.aps === 'object' && data.aps.url) {
        return routeForAction(data.aps.action || data.action, data.aps.url);
    }
    // Arbitrary named route
    if (data.route) {
        return typeof data.route === 'string'
            ? { path: data.route }
            : data.route;
    }
    return null;
}

async function persistToken(token) {
    if (!token) return;
    await Preferences.set({ key: KEY_TOKEN, value: token });
    await Preferences.set({
        key: KEY_PLATFORM,
        value: nativePlatform() || ''
    });
}

async function readStoredDeviceParams() {
    const [{ value: token }, { value: platform }] = await Promise.all([
        Preferences.get({ key: KEY_TOKEN }),
        Preferences.get({ key: KEY_PLATFORM })
    ]);
    if (!token || !platform) return null;
    return { platform, token };
}

async function readPendingDeepLinks() {
    const { value } = await Preferences.get({ key: KEY_PENDING_DEEP_LINK });
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        // Back-compat: older builds stored a single target object rather
        // than an array. Normalise so callers can always map over it.
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') return [parsed];
        return [];
    } catch (_) {
        return [];
    }
}

async function replayPendingDeepLink() {
    const pending = await readPendingDeepLinks();
    await Preferences.remove({ key: KEY_PENDING_DEEP_LINK });
    if (!pending.length) return;
    // Only the most recent deep link actually places the user on the
    // intended screen; earlier taps would just flash past. Replay the
    // last one so we don't discard user intent silently but also don't
    // whip them through a stack of pushes.
    const target = pending[pending.length - 1];
    router.push(target).catch(() => {});
}

async function handleNotificationOpened(notification, { store } = {}) {
    const data = notification?.data || notification?.notification?.data || {};
    const target = routeFromPayload(data);
    if (!target) return;

    const isAuthed = !!store?.getters?.['auth/user']?.id;
    if (isAuthed) {
        router.push(target).catch(() => {});
    } else {
        const pending = await readPendingDeepLinks();
        pending.push(target);
        // Cap at a handful so a runaway sender can't fill Preferences.
        const trimmed = pending.slice(-5);
        await Preferences.set({
            key: KEY_PENDING_DEEP_LINK,
            value: JSON.stringify(trimmed)
        });
    }
}

async function attachListeners({ store } = {}) {
    if (listenersAttached) return;
    listenersAttached = true;

    PushNotifications.addListener('registration', async token => {
        // On iOS `token.value` is the APNs device token (hex string); on
        // Android it is the FCM registration token. Both are what the
        // backend's AWS SNS platform endpoints expect.
        const prev = (await Preferences.get({ key: KEY_TOKEN })).value;
        await persistToken(token.value);
        // Token rotated under us: invalidate the "registered for user"
        // marker so the next registerDevice forces a fresh POST even if
        // the user id hasn't changed.
        if (prev && prev !== token.value) {
            await Preferences.remove({ key: KEY_REGISTERED_USER });
        }

        const user = store?.getters?.['auth/user'];
        if (user && user.id) {
            registerDevice({ userId: user.id }).catch(err =>
                console.warn('[push] registerDevice failed', err)
            );
        }
    });

    PushNotifications.addListener('registrationError', err => {
        console.warn('[push] registration error', err?.error || err);
    });

    PushNotifications.addListener('pushNotificationReceived', notification => {
        // Foreground message. Do not route; surface an in-app snackbar via
        // the existing Snackbar component if the store has an action for it.
        if (store?.dispatch) {
            const title =
                notification.title ||
                notification.notification?.title ||
                'New notification';
            const body =
                notification.body || notification.notification?.body || '';
            try {
                // The snackbar store's `show` action expects { text }, not
                // { message } — passing the wrong shape silently rendered an
                // empty snackbar. `showMessage` is the convenience action
                // already used by Handler.handleResponse for consistency.
                store.dispatch(
                    'snackbar/showMessage',
                    title + (body ? ' — ' + body : ''),
                    { root: true }
                );
            } catch (_) {
                // Snackbar module may not be wired — swallow.
            }
        }
    });

    PushNotifications.addListener('pushNotificationActionPerformed', action => {
        handleNotificationOpened(action.notification, { store });
    });
}

async function ensurePermission() {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await PushNotifications.requestPermissions();
    }
    await Preferences.set({
        key: KEY_PERMISSION,
        value: perm.receive || 'denied'
    });
    return perm.receive === 'granted';
}

/**
 * Attach the `App.resume` handler exactly once. Kept separate from
 * `initPushnotifications` so it runs even when permission is denied at
 * first boot — the user may later flip the toggle in Settings, and when
 * they come back to the app we want to pick up the grant without a
 * full app restart.
 */
async function ensureResumeHandler({ store } = {}) {
    if (resumeHandlerAttached) return;
    resumeHandlerAttached = true;

    App.addListener('resume', async () => {
        try {
            const perm = await PushNotifications.checkPermissions();
            const prevPerm = (await Preferences.get({ key: KEY_PERMISSION }))
                .value;
            await Preferences.set({
                key: KEY_PERMISSION,
                value: perm.receive || 'denied'
            });

            // Permission was revoked while we were backgrounded. Drop
            // the backend registration so we don't keep a stale endpoint
            // alive server-side.
            if (prevPerm === 'granted' && perm.receive !== 'granted') {
                await removePushnotifications();
                return;
            }

            if (perm.receive === 'granted') {
                // Covers two cases:
                //   1. prevPerm was 'granted' — routine token refresh to
                //      catch FCM/APNs rotation or a disabled endpoint.
                //   2. prevPerm was 'denied' / 'prompt' — user just flipped
                //      the toggle in Settings; bootstrap the pipeline now
                //      without waiting for an app restart.
                await PushNotifications.register();
                const user =
                    store && store.getters && store.getters['auth/user'];
                if (user && user.id) {
                    registerDevice({
                        userId: user.id,
                        force: true
                    }).catch(err =>
                        console.warn('[push] resume register failed', err)
                    );
                }
            }
        } catch (err) {
            console.warn('[push] resume refresh failed', err);
        }
    });
}

/**
 * Initialise the push pipeline. Safe to call unconditionally — returns a
 * resolved promise on non-native platforms.
 */
async function initPushnotifications({ store } = {}) {
    if (!isNative()) return { ok: true, skipped: 'web' };

    // iOS Simulator never returns a real APNs token — skip registration so
    // the UI doesn't sit in a perpetual "registering…" state during dev.
    try {
        const info = await Device.getInfo();
        if (info.isVirtual && info.platform === 'ios') {
            return { ok: true, skipped: 'ios-simulator' };
        }
    } catch (_) {
        /* ignore — Device.getInfo failures are non-fatal */
    }

    await attachListeners({ store });

    // Attach the resume handler BEFORE the permission gate so the app can
    // recover on its own if the user denies now and flips the Settings
    // toggle later — without this the only way to bootstrap push after a
    // denial would be a full app restart.
    await ensureResumeHandler({ store });

    const granted = await ensurePermission();
    if (!granted) return { ok: false, reason: 'permission-denied' };

    // register() can throw at the native layer if Firebase isn't fully
    // configured (e.g. missing or placeholder google-services.json on a dev
    // build). Don't let that crash the whole app — log it and keep going so
    // the rest of the UI is still usable.
    try {
        await PushNotifications.register();
    } catch (err) {
        console.warn('[push] register() failed', err);
        return { ok: false, reason: 'register-failed', error: err };
    }

    await replayPendingDeepLink();
    return { ok: true };
}

/**
 * Register the currently stored device token with the backend.
 *
 * Short-circuits if we already POSTed this exact token for this user id
 * since the last logout / token rotation, so burst calls (auth hook +
 * registration listener + app resume) don't thrash the server.
 *
 * @param {Object} [opts]
 * @param {string} [opts.userId] Current authenticated user id. If omitted
 *     the POST still goes through but we skip the "already registered"
 *     short-circuit and don't persist the registered-user marker.
 * @param {boolean} [opts.force] Bypass the short-circuit (used on resume
 *     after long backgrounds to catch server-side drop).
 */
async function registerDevice({ userId, force = false } = {}) {
    if (!isNative()) return { ok: true, skipped: 'web' };

    // De-dupe concurrent calls. Return the same promise so callers don't
    // see interleaved responses.
    if (registerInFlight) return registerInFlight;

    registerInFlight = (async () => {
        try {
            const params = await readStoredDeviceParams();
            if (!params) return { ok: false, reason: 'no-token' };

            if (!force && userId) {
                const { value: registeredFor } = await Preferences.get({
                    key: KEY_REGISTERED_USER
                });
                if (registeredFor === userId) {
                    return { ok: true, skipped: 'already-registered' };
                }
            }

            try {
                const resp = await notifications.registerDevice(params);
                if (resp && resp.ok) {
                    if (userId) {
                        await Preferences.set({
                            key: KEY_REGISTERED_USER,
                            value: userId
                        });
                    }
                    return { ok: true, resp };
                }
                return { ok: false, resp };
            } catch (err) {
                console.warn('[push] registerDevice request failed', err);
                return { ok: false, error: err };
            }
        } finally {
            registerInFlight = null;
        }
    })();

    return registerInFlight;
}

/**
 * Unregister the device server-side and drop the "registered for user"
 * marker so the next login re-POSTs. Called from the auth/logout flow.
 *
 * We deliberately keep `KEY_TOKEN` + `KEY_PLATFORM` in Preferences. The
 * OS-issued APNs / FCM token is a device-level identifier that outlives
 * user sessions — clearing it here would mean a subsequent `registerDevice`
 * after login-as-different-user has no token to POST until the app next
 * resumes and `PushNotifications.register()` re-emits it. Keeping the
 * token lets the next login flow immediately re-POST; the server then
 * either reassigns ownership (if it still has the row) or creates a new
 * one (we just DELETEd it).
 *
 * Always clears local state even if the DELETE fails — the auth bearer
 * is about to be dropped so retrying later would 401 anyway.
 */
async function removePushnotifications() {
    if (!isNative()) return { ok: true, skipped: 'web' };
    const params = await readStoredDeviceParams();
    let deleteOk = true;
    if (params) {
        try {
            const resp = await notifications.unregisterDevice(params);
            deleteOk = !!(resp && resp.ok);
            if (!deleteOk) {
                console.warn('[push] unregisterDevice non-ok response', resp);
            }
        } catch (err) {
            deleteOk = false;
            console.warn('[push] unregisterDevice request failed', err);
        }
    }
    // Drop the "registered for user" marker so the next login force-POSTs.
    await Preferences.remove({ key: KEY_REGISTERED_USER });
    // Clear any of the previous user's notifications still sitting in the
    // system tray so they don't leak to whoever logs in next on this device.
    try {
        await PushNotifications.removeAllDeliveredNotifications();
    } catch (_) {
        /* plugin may not implement this on every platform — non-fatal */
    }
    return { ok: deleteOk };
}

export default {
    initPushnotifications,
    registerDevice,
    removePushnotifications
};
